import React, { useState, useEffect } from 'react';
import { MdClose, MdSearch, MdCalendarToday } from 'react-icons/md';
import { FaMapPin } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axiosPost from '../helpers/axios-helper';
import '../styles/EventsModal.css';

export default function EventsModal({ isOpen, onClose }) {
  const [events, setEvents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Fetch countries
  useEffect(() => {
    const controller = new AbortController();
    axiosPost(
      'countries/list',
      {},
      { active: 1 },
      (res) => {
        const items = res?.data || res || [];
        setCountries(Array.isArray(items) ? items : []);
      },
      controller
    );
    return () => controller.abort();
  }, []);

  // Fetch events
  useEffect(() => {
    if (!isOpen) return;
    
    const controller = new AbortController();
    setLoading(true);

    const payload = {
      per_page: 50,
      active: 1
    };

    // Add country filter if selected
    if (selectedCountry) {
      payload.country_id = selectedCountry;
    }

    // Fetch only future events
    const today = Math.floor(Date.now() / 1000);
    payload.from_date = today;

    axiosPost(
      'events/list',
      {},
      payload,
      (res) => {
        const items = res?.data?.items || [];
        setEvents(items);
        setLoading(false);
      },
      controller
    );

    return () => controller.abort();
  }, [isOpen, selectedCountry]);

  // Handle animation timing
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Trigger animation after render
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Remove from DOM after animation completes
      const timer = setTimeout(() => setShouldRender(false), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  const formatEventDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const weekday = date.toLocaleString('en-US', { weekday: 'short' });
    return { day, month, weekday };
  };

  const formatDateRange = (startTimestamp, endTimestamp) => {
    const start = new Date(startTimestamp * 1000);
    const end = endTimestamp ? new Date(endTimestamp * 1000) : null;
    
    const startDay = start.getDate();
    const startMonth = start.toLocaleString('en-US', { month: 'short' });
    
    if (end && end.getTime() !== start.getTime()) {
      const endDay = end.getDate();
      const endMonth = end.toLocaleString('en-US', { month: 'short' });
      
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay} - ${endDay}`;
      } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
      }
    }
    
    return `${startMonth} ${startDay}`;
  };

  const slugify = (str) => {
    try {
      return (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch { return ''; }
  };

  // Filter events by search term
  const filteredEvents = events.filter(event => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      event.title?.toLowerCase().includes(search) ||
      event.location?.toLowerCase().includes(search) ||
      event.description?.toLowerCase().includes(search)
    );
  });

  if (!shouldRender) return null;

  return (
    <>
      <div className={`events-modal-overlay ${isAnimating ? 'open' : ''}`} onClick={onClose} />
      <div className={`events-modal ${isAnimating ? 'open' : ''}`}>
        <div className="events-modal-header">
          <div className="events-modal-title">
            <MdCalendarToday size={24} />
            <h2>UPCOMING EVENTS</h2>
          </div>
          <button className="events-modal-close" onClick={onClose}>
            <MdClose size={28} />
          </button>
        </div>

        <div className="events-modal-content">
          {/* Filters Section */}
          <div className="events-filters-section">
            <div className="events-search-bar">
              <MdSearch size={20} />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="events-country-filter">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                <option value="">All Countries</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Events List */}
          <div className="events-list-section">
            {loading ? (
              <div className="events-loading">Loading events...</div>
            ) : filteredEvents.length === 0 ? (
              <div className="events-empty">No events found</div>
            ) : (
              filteredEvents.map((event) => {
                const isLgbtq = event.event_type === 'lgbtq';
                const dateInfo = formatEventDate(event.event_date);
                const dateRange = formatDateRange(event.event_date, event.end_date);

                return (
                  <Link
                    key={event.id}
                    to={`/event/${slugify(event.title)}`}
                    className="modal-event-item"
                    onClick={onClose}
                  >
                    <div className="modal-event-image">
                      {event.image_url && (
                        <img src={event.image_url} alt={event.title} />
                      )}
                      {isLgbtq && (
                        <div className="modal-event-badge">
                          <span className="lgbtq-indicator" />
                        </div>
                      )}
                    </div>

                    <div className="modal-event-details">
                      <div className="modal-event-date">
                        <span className="modal-event-weekday">{dateInfo.weekday.toUpperCase()}</span>
                        <span className="modal-event-date-range">{dateRange}</span>
                      </div>
                      
                      <h4 className="modal-event-title">{event.title}</h4>
                      
                      {event.location && (
                        <div className="modal-event-location">
                          <FaMapPin size={12} />
                          <span>{event.location}</span>
                        </div>
                      )}

                      {event.description && (
                        <p className="modal-event-description">{event.description}</p>
                      )}
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}
